export const SOCIAL_CHANNEL = {
  facebook: 'facebook',
  instagram: 'instagram',
  linkedin: 'linkedin',
  linkedinSocial: 'linkedin_social',
  twitter: 'twitter',
  google: 'google',
  youtube: 'youtube',
  pinterest: 'pinterest',
  facebookPages: 'facebook_pages',
  twitterSocial: 'twitter_social',
};

export const NATIVE_CHANNEL = {
  taboola: 'taboola',
  outbrain: 'outbrain',
  revcontent: 'revcontent',
  verizon: 'verizon',
  amazon: 'amazon',
  walmart: 'walmart',
  teads: 'teads',
};

export const REVIEWS_CHANNEL = {
  trustpilot: 'trustpilot',
  amazon: 'amazon',
  g2: 'g2',
};

export const WEBSITE_CHANNEL = {
  homepage: 'home_page',
  productPage: 'product_page',
  landingPage: 'landing_page',
  competitors: 'competitors',
  thoughtLeadership: 'thought_leadership',
  other: 'other',
};

export const EMAILS_CHANNEL = {
  salesforce: 'salesforce',
  hubspot: 'hubspot',
  marketo: 'marketo',
  braze: 'braze',
  eloqua: 'eloqua_emails',
  iterable: 'iterable_emails',
  salesforceExactTarget: 'salesforce_exact_target',
};

export const PLATFORM_CHANNEL = {
  projectWtm: 'project_wtm',
  blogUrls: 'blog_urls',
  generalDocument: 'general_document',
  emailChannel: 'email_channel',
  general: 'general',
};

export const BETA_CHANNEL = {
  gong: 'gong',
  oktopost: 'oktopost',
  adobeExperienceManager: 'adobe_experience_manager',
  outreach: 'outreach',
  salsify: 'salsify',
  microsoftWord: 'microsoft_word',
  sprinklr: 'sprinklr',
  inriver: 'inriver',
  sitecore: 'sitecore',
  wrike: 'wrike',
};

export const EXTERNAL_CHANNEL = {
  salesforceCrm: 'salesforce_crm',
  microsoftDynamicsSales: 'microsoft_dynamics_sales',
};

export const EXPORT_CHANNEL = {
  salesforceExport: 'salesforce_exact_target_export',
  csvExport: 'csv_export',
  sprinklrExport: 'sprinklr_export',
};

/**
 * Channels combined enum
 */
export const CHANNEL = {
  ...SOCIAL_CHANNEL,
  ...NATIVE_CHANNEL,
  ...REVIEWS_CHANNEL,
  ...WEBSITE_CHANNEL,
  ...EXTERNAL_CHANNEL,
  ...EMAILS_CHANNEL,
  ...PLATFORM_CHANNEL,
  ...BETA_CHANNEL,
  ...EXPORT_CHANNEL,
} as const;
export type Channel = (typeof CHANNEL)[keyof typeof CHANNEL];

interface ChannelProps {
  label: string;
  performanceLabel?: string;
  talkingPointLabel?: string;
  performanceShortLabel?: string;
  description?: string;
}

const CHANNEL_PROPS: Record<Channel, ChannelProps> = {
  [CHANNEL.facebook]: {
    label: 'Facebook',
    performanceLabel: 'Meta ads',
    talkingPointLabel: 'Meta ads',
    performanceShortLabel: 'Meta',
    description:
      "Meta ad accounts hold valuable data about what drives engagement and conversion at both the top and bottom of your sales funnel. Connect your account to optimize your Meta ads' effectiveness and enhance content creation across all your marketing channels.",
  },
  [CHANNEL.facebookPages]: {
    label: 'Facebook',
    performanceLabel: 'Facebook page',
    talkingPointLabel: 'Facebook page',
    performanceShortLabel: 'Facebook',
    description:
      'Facebook page data is key to understanding what drives audience engagement and interaction. Connect your account to boost engagement on Facebook and enhance content generation across all your marketing channels.',
  },
  [CHANNEL.instagram]: {
    label: 'Instagram',
    performanceLabel: 'Instagram account',
    talkingPointLabel: 'Instagram',
    performanceShortLabel: 'Instagram',
    description:
      'Instagram accounts hold valuable insights into what content resonates with your audience and drives engagement. Connect your account to boost engagement on Instagram and enhance content creation across all your marketing channels.',
  },
  [CHANNEL.twitter]: {
    label: 'X',
  },

  [CHANNEL.twitterSocial]: {
    label: 'X',
    performanceLabel: 'X (Twitter) account',
    talkingPointLabel: 'X (Twitter)',
    performanceShortLabel: 'X',
    description:
      'X accounts hold valuable insights on what content engages your followers. Connect your account to enhance the performance of your LinkedIn posts and boost content creation across all your channels.',
  },
  [CHANNEL.pinterest]: {
    label: 'Pinterest',
  },
  [CHANNEL.linkedin]: {
    label: 'LinkedIn',
    performanceLabel: 'LinkedIn ads',
    talkingPointLabel: 'LinkedIn ads',
    performanceShortLabel: 'LinkedIn',
    description:
      "LinkedIn ad accounts hold valuable data about what drives engagement and conversion at both the top and bottom of your sales funnel. Connect your account to optimize your LinkedIn ads' effectiveness and enhance content creation across all your marketing channels.",
  },
  [CHANNEL.linkedinSocial]: {
    label: 'LinkedIn',
    performanceLabel: 'LinkedIn page',
    talkingPointLabel: 'LinkedIn page',
    performanceShortLabel: 'LinkedIn',
    description:
      'LinkedIn pages hold valuable insights on what content engages your followers. Connect your account to enhance the performance of your LinkedIn posts and boost content creation across all your channels.',
  },
  [CHANNEL.google]: {
    label: 'Google',
    performanceLabel: 'Google ads',
    talkingPointLabel: 'Google ads',
    performanceShortLabel: 'Google',
    description:
      'Google ad accounts are a treasure trove of insights into what drives direct responses for a product or service. Connect your account to optimize the effectiveness of Google ads and elevate content creation across all your marketing channels.',
  },
  [CHANNEL.youtube]: {
    label: 'YouTube',
  },
  [CHANNEL.taboola]: {
    label: 'Taboola',
  },
  [CHANNEL.outbrain]: {
    label: 'Outbrain',
  },
  [CHANNEL.revcontent]: {
    label: 'Revcontent',
  },
  [CHANNEL.teads]: {
    label: 'Teads',
  },
  [CHANNEL.verizon]: {
    label: 'Verizon Media',
  },
  [CHANNEL.amazon]: {
    label: 'Amazon',
  },
  [CHANNEL.walmart]: {
    label: 'Walmart',
  },
  [CHANNEL.trustpilot]: {
    label: 'Trustpilot',
    talkingPointLabel: 'Webpage',
    performanceLabel: 'Trustpilot reviews',
    description:
      'Make sure your messaging reflects the voice of your most satisfied customers by importing reviews and high-performing talking points.',
  },
  [CHANNEL.amazon]: {
    label: 'Amazon',
    talkingPointLabel: 'Webpage',
    performanceLabel: 'Amazon reviews',
    description:
      'Make sure your messaging reflects the voice of your most satisfied customers by importing reviews and high-performing talking points.',
  },
  [CHANNEL.g2]: {
    label: 'G2',
    performanceLabel: 'G2 reviews',
    description: '',
  },
  [CHANNEL.homepage]: {
    label: 'Homepage',
    talkingPointLabel: 'Webpage',
    description:
      'Connect your homepage for more effective, on-brand content generation. Use it as a source to set your brand voice and access talking points that resonate most with your audience.',
  },
  [CHANNEL.landingPage]: {
    label: 'Landing page',
    talkingPointLabel: 'Webpage',
    description:
      'Connect your landing pages for more effective, on-brand content generation. Use it as a source to set your brand voice and access talking points that resonate most with your audience.',
  },
  [CHANNEL.competitors]: {
    label: 'Competitor',
    talkingPointLabel: 'Webpage',
    performanceLabel: 'Competitor website',
    description:
      'Uncover your competitors brand voice and discover their most impactful words for a better understanding.',
  },
  [CHANNEL.thoughtLeadership]: {
    label: 'Thought leadership',
    talkingPointLabel: 'Webpage',
    description:
      'Get powerful talking points from industry thought leadership (articles, blogs...) to make your content more engaging.',
  },
  [CHANNEL.productPage]: {
    label: 'Product information',
    talkingPointLabel: 'Webpage',
    description:
      'Get powerful talking points from industry product page (articles, blogs...) to make your content more engaging.',
  },
  [CHANNEL.other]: {
    label: 'Webpage',
    performanceLabel: 'Webpage',
    talkingPointLabel: 'Webpage',
    performanceShortLabel: 'Webpage',
    description: '',
  },
  [CHANNEL.hubspot]: {
    label: 'HubSpot',
    performanceLabel: 'HubSpot',
    description:
      'HubSpot accounts house email performance metrics. Connect your account to enhance the effectiveness of both one-to-one and one-to-many email campaigns.',
    talkingPointLabel: 'HubSpot',
  },
  [CHANNEL.salesforce]: {
    label: 'Salesforce Marketing Cloud',
    performanceLabel: 'Salesforce Marketing Cloud',
    description:
      'Salesforce Marketing Cloud accounts house email performance metrics. Connect your account to enhance the effectiveness of both one-to-one and one-to-many email campaigns.',
    talkingPointLabel: 'Salesforce Marketing Cloud',
  },
  [CHANNEL.salesforceCrm]: {
    label: 'Salesforce',
    performanceLabel: 'Salesforce',
    description:
      'Connect your Salesforce account to enrich prompts and templates with your Salesforce data, including information from accounts, opportunities, contacts, and more.',
  },
  [CHANNEL.microsoftDynamicsSales]: {
    label: 'Microsoft Dynamic 365 sales',
    performanceLabel: 'Microsoft Dynamic 365 sales',
    description:
      'Connect your Microsoft Dynamics 365 Sales account to enrich prompts and templates with your Dynamics 365 Sales data, including information from accounts, contacts, and more.',
  },
  [CHANNEL.marketo]: {
    label: 'Marketo',
    performanceLabel: 'Marketo',
    description:
      'Marketo accounts house email performance metrics. Connect your account to enhance the effectiveness of both one-to-one and one-to-many email campaigns.',
    talkingPointLabel: 'Marketo',
  },
  [CHANNEL.braze]: {
    label: 'Braze',
    performanceLabel: 'Braze',
    description:
      'Braze accounts house email performance metrics. Connect your account to enhance the effectiveness of both one-to-one and one-to-many email campaigns.',
    talkingPointLabel: 'Braze',
  },
  [CHANNEL.projectWtm]: {
    label: 'Website',
    description:
      "Your website's text might not be fully optimized to maximize its conversion potential. Integrate Anyword's code to effortlessly test various text versions and enhance your conversion rate.",
  },
  [CHANNEL.blogUrls]: {
    label: 'Blog posts and articles',
  },
  [CHANNEL.generalDocument]: {
    label: 'File',
    talkingPointLabel: 'File',
  },
  [CHANNEL.emailChannel]: {
    label: 'Email',
    performanceLabel: 'Email performance report (CSV)',
    talkingPointLabel: 'Email performance report (CSV)',
    description:
      'Upload your email campaign data from any platform, and Anyword will adapt to your unique tone, optimizing newly generated emails for enhanced performance.',
  },
  [CHANNEL.general]: {
    label: 'General',
  },
  [CHANNEL.gong]: {
    label: 'Gong',
    description:
      "Gong's call recordings hold hidden gems—analyze and apply them to generate higher-performing content across all your marketing channels.",
  },
  [CHANNEL.eloqua]: {
    label: 'Eloqua Marketing Automation',
    description:
      'Connect your Eloqua Marketing Automation account, and Anyword will learn your writing style. Historical data from your account will be used to help create new highly converting content as well as improve existing running.',
  },
  [CHANNEL.salesforceExactTarget]: {
    label: 'Salesforce Marketing Cloud (ExactTarget)',
    description:
      'Salesforce Marketing Cloud accounts house email performance metrics. Connect your account to enhance the effectiveness of both one-to-one and one-to-many email campaigns.',
  },
  [CHANNEL.oktopost]: {
    label: 'Oktopost',
    description:
      'Publish content from Anyword directly to Oktopost, allowing for a smoother process and better alignment with your existing workflows.',
  },
  [CHANNEL.adobeExperienceManager]: {
    label: 'Adobe Experience Manager',
    description:
      'Publish content from Anyword directly to Adobe Experience Manager, allowing for a smoother process and better alignment with your existing workflows.',
  },
  [CHANNEL.outreach]: {
    label: 'Outreach',
    description:
      'Outreach accounts house email performance metrics. Connect your account to enhance the effectiveness of both one-to-one and one-to-many email campaigns.',
  },
  [CHANNEL.salsify]: {
    label: 'Salsify',
    description:
      'Connect your Salsify product catalog to Anyword to improve product descriptions, meta data, and SEO content easily.',
  },
  [CHANNEL.microsoftWord]: {
    label: 'Microsoft Word',
    description:
      'Use Anyword within Microsoft Word and access all of Anyword’s unique features directly in the familiar Word interface.',
  },
  [CHANNEL.iterable]: {
    label: 'Iterable',
    description:
      'Iterable accounts house email performance metrics. Connect your account to enhance the effectiveness of both one-to-one and one-to-many email campaigns.',
  },
  [EXPORT_CHANNEL.salesforceExport]: {
    label: 'Salesforce Marketing Cloud',
    description:
      'Export generated marketing assets to Salesforce Marketing Cloud, automating content publishing.',
  },
  [EXPORT_CHANNEL.csvExport]: {
    label: 'CSV',
    description:
      'Export generated marketing assets to CSV, allowing for easy integration with other platforms and tools.',
  },
  [EXPORT_CHANNEL.sprinklrExport]: {
    label: 'Sprinklr',
    description: 'Export generated marketing assets to Sprinklr, automating content publishing.',
  },
  [BETA_CHANNEL.sprinklr]: {
    label: 'Sprinklr',
    description: 'Export generated marketing assets to Sprinklr, automating content publishing.',
  },
  [BETA_CHANNEL.inriver]: {
    label: 'InRiver',
    description:
      'Connect your InRiver product catalog to Anyword to improve product descriptions, meta data, and SEO content easily.',
  },
  [BETA_CHANNEL.sitecore]: {
    label: 'Sitecore',
    description:
      'Connect your Sitecore account to enrich prompts and templates with your Sitecore account data, including information from accounts, contacts, and more.  ',
  },
  [BETA_CHANNEL.wrike]: {
    label: 'Wrike',
    description: 'Export generated marketing assets to Wrike, automating content publishing.',
  },
};

export const getChannelLabel = (channel: Channel): string => {
  const props = CHANNEL_PROPS[channel];
  return props?.label || '';
};

export const getTalkingPointChannelLabel = (channel: Channel): string => {
  const props = CHANNEL_PROPS[channel];
  return props?.talkingPointLabel || '';
};

export const getChannelDescription = (channel: Channel): string => {
  const props = CHANNEL_PROPS[channel];
  return props?.description || '';
};

export const getChannelPerformanceLabel = (channel: Channel): string => {
  const props = CHANNEL_PROPS[channel];
  return props?.performanceLabel || props?.label || '';
};

export const getChannelPerformanceShortLabel = (channel: Channel): string => {
  const props = CHANNEL_PROPS[channel];
  return props?.performanceShortLabel || props?.performanceLabel || props?.label || '';
};

const BACKEND_CHANNEL = {
  hubspot: 'hubspot_emails',
  instagram: 'instagram_account',
  trustpilot: 'reviews_trustpilot',
  amazon: 'reviews_amazon',
  salesforce: 'salesforce_emails',
  marketo: 'marketo_emails',
  braze: 'braze_emails',
  salesforceExactTarget: 'salesforce_exact_target_emails',
} as const;
export type BackendChannel = (typeof BACKEND_CHANNEL)[keyof typeof BACKEND_CHANNEL];

const BACKEND_CHANNELS_MAPPER = {
  [CHANNEL.hubspot]: BACKEND_CHANNEL.hubspot,
  [CHANNEL.instagram]: BACKEND_CHANNEL.instagram,
  [CHANNEL.trustpilot]: BACKEND_CHANNEL.trustpilot,
  [CHANNEL.amazon]: BACKEND_CHANNEL.amazon,
  [CHANNEL.salesforce]: BACKEND_CHANNEL.salesforce,
  [CHANNEL.marketo]: BACKEND_CHANNEL.marketo,
  [CHANNEL.braze]: BACKEND_CHANNEL.braze,
  [CHANNEL.salesforceExactTarget]: BACKEND_CHANNEL.salesforceExactTarget,
};

const FRONTEND_CHANNELS_MAPPER = {
  [BACKEND_CHANNEL.hubspot]: CHANNEL.hubspot,
  [BACKEND_CHANNEL.instagram]: CHANNEL.instagram,
  [BACKEND_CHANNEL.trustpilot]: CHANNEL.trustpilot,
  [BACKEND_CHANNEL.amazon]: CHANNEL.amazon,
  [BACKEND_CHANNEL.salesforce]: CHANNEL.salesforce,
  [BACKEND_CHANNEL.marketo]: CHANNEL.marketo,
  [BACKEND_CHANNEL.braze]: CHANNEL.braze,
  [BACKEND_CHANNEL.salesforceExactTarget]: CHANNEL.salesforceExactTarget,
};

export const alignChannelWithBackend = (channel: Channel): BackendChannel =>
  BACKEND_CHANNELS_MAPPER[channel] || channel;

export const alignChannelFromBackend = (channel: BackendChannel): Channel =>
  FRONTEND_CHANNELS_MAPPER[channel] || channel;

export const alignChannelForUnauthorize = (channel: Channel): string =>
  [CHANNEL.facebookPages, CHANNEL.instagram].includes(channel)
    ? CHANNEL.facebook
    : alignChannelWithBackend(channel);

export const WEBSITE_CHANNELS = Object.values(WEBSITE_CHANNEL);
export const EMAILS_CHANNELS = Object.values(EMAILS_CHANNEL);
export const PLATFORM_CHANNELS = Object.values(PLATFORM_CHANNEL);
export const BETA_CHANNELS = Object.values(BETA_CHANNEL);
export const EXTERNAL_CHANNELS = Object.values(EXTERNAL_CHANNEL);
export const EXPORT_CHANNELS = Object.values(EXPORT_CHANNEL);

export const ADS_CHANNELS = [CHANNEL.google, CHANNEL.facebook, CHANNEL.linkedin];
export const REVIEWS_CHANNELS = [REVIEWS_CHANNEL.amazon, REVIEWS_CHANNEL.trustpilot];
export const SOCIAL_CHANNELS = [CHANNEL.linkedinSocial, CHANNEL.facebookPages, CHANNEL.instagram];

export const INTEGRATION_CHANNELS = [
  ...EMAILS_CHANNELS,
  ...ADS_CHANNELS,
  ...SOCIAL_CHANNELS,
  CHANNEL.twitterSocial,
  PLATFORM_CHANNEL.projectWtm,
];

export const RESOURCE_CHANNELS = [...WEBSITE_CHANNELS, ...REVIEWS_CHANNELS];

export const isIntegrationChannel = (channel: Channel): boolean =>
  INTEGRATION_CHANNELS.includes(channel);
export const isPlatformChannel = (channel: Channel): boolean => PLATFORM_CHANNELS.includes(channel);
export const isExternalChannel = (channel: Channel): boolean => EXTERNAL_CHANNELS.includes(channel);
export const isAdChannel = (channel: Channel): boolean => ADS_CHANNELS.includes(channel);
export const isExportChannel = (channel: Channel): boolean =>
  EXPORT_CHANNELS.includes(channel) || [BETA_CHANNEL.wrike].includes(channel);
export const isBetaChannel = (channel: Channel): boolean => BETA_CHANNELS.includes(channel);
export const isSocialChannel = (channel: Channel): boolean => SOCIAL_CHANNELS.includes(channel);

export const isBlogUrlsChannel = (channel: Channel): boolean => channel === CHANNEL.blogUrls;
export const isMetaChannel = (channel: Channel): boolean =>
  [CHANNEL.facebook, CHANNEL.facebookPages, CHANNEL.instagram].includes(channel);

export const shouldChannelConnectManually = (channel: Channel): boolean =>
  [CHANNEL.marketo, CHANNEL.braze].includes(channel);
